import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import { DxButtonModule, DxDataGridModule } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { firstValueFrom } from 'rxjs';
import {
	BulkUpdateChangelogReleaseDto,
	ChangelogDetailDto,
	ChangelogReleaseEditorDto,
	ChangelogReleaseOverviewDto,
	ChangelogReleasePreviewDto,
	ChangelogReleaseSourceDto,
	ChangelogService,
	ChangelogTargetApplicationOptionDto,
	GroupedChangelogItemDto,
	GroupedChangelogPayloadDto,
	GroupedChangelogSectionDto,
} from '../../../services/changelog.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';

type ChangelogReleaseOverviewRow = ChangelogReleaseOverviewDto & {
	selectionKey: string;
	sourceApplicationsText: string;
	highlightsText: string;
};

@Component({
	selector: 'app-shell-admin-changelog-management',
	standalone: true,
	imports: [
		AdminWarningBannerComponent,
		CommonModule,
		FormsModule,
		DxButtonModule,
		DxDataGridModule,
	],
	templateUrl: './admin-changelog-management.component.html',
	styleUrls: ['./admin-changelog-management.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminChangelogManagementComponent implements OnInit {
	public filterReleaseFamilyKey = '';
	public includePreRelease = false;
	public take = 50;

	public overviewRows: ChangelogReleaseOverviewRow[] = [];
	public selectedRowKeys: string[] = [];

	public selectedOverview?: ChangelogReleaseOverviewRow;
	public release?: ChangelogReleaseEditorDto;
	public editorPayload?: GroupedChangelogPayloadDto;
	public preview?: ChangelogReleasePreviewDto;

	public selectedSourceId?: number;
	public selectedSourceDetail?: ChangelogDetailDto;

	public previewAudience = 'Users';
	public previewStage = 'Draft';
	public previewApplicationKeys: string[] = [];
	public selectedSourceIds: number[] = [];

	public selectedBulkItemIds: string[] = [];
	public bulkSectionName = '';
	public bulkVisibility = '';
	public bulkScope = '';
	public bulkTargetApplicationKeys: string[] = [];

	public loadingOverview = false;
	public loadingEntry = false;
	public loadingPreview = false;
	public loadingSourceDetail = false;
	public saving = false;
	public applyingBulkUpdate = false;
	public isDirty = false;

	constructor(
		private changelogService: ChangelogService,
		private changeDetectorRef: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		void this.loadOverview();
	}

	public get hasSelection(): boolean {
		return this.selectedOverview != null && this.release != null && this.editorPayload != null;
	}

	public get canApplyBulkUpdate(): boolean {
		return this.hasSelection
			&& !this.isDirty
			&& !this.applyingBulkUpdate
			&& this.selectedBulkItemIds.length > 0
			&& (!!this.bulkSectionName || !!this.bulkVisibility || !!this.bulkScope || this.bulkTargetApplicationKeys.length > 0);
	}

	public get allowedSections(): string[] {
		return this.release?.allowedSections ?? ['Added', 'Changed', 'Fixed', 'Removed', 'Deprecated', 'Security', 'Notes'];
	}

	public get allowedVisibilityTargets(): string[] {
		return this.release?.allowedVisibilityTargets ?? ['Hide', 'Superadmins', 'Users'];
	}

	public get allowedScopes(): string[] {
		return this.release?.allowedScopes ?? ['Global', 'Application'];
	}

	public get allowedPreviewAudiences(): string[] {
		return this.release?.allowedPreviewAudiences ?? ['Users', 'Superadmins'];
	}

	public get availableApplications(): ChangelogTargetApplicationOptionDto[] {
		return this.release?.availableApplications ?? [];
	}

	public get selectedSource(): ChangelogReleaseSourceDto | undefined {
		return this.release?.availableSources.find(source => source.id === this.selectedSourceId)
			?? this.release?.sources.find(source => source.id === this.selectedSourceId);
	}

	public async loadOverview(preferredSelection?: { releaseFamilyKey: string; version: string; isPreRelease: boolean }): Promise<void> {
		this.loadingOverview = true;
		this.changeDetectorRef.markForCheck();

		try {
			const overview = await firstValueFrom(this.changelogService.getReleaseOverview({
				releaseFamilyKey: this.filterReleaseFamilyKey.trim() || undefined,
				includePreRelease: this.includePreRelease,
				take: this.take,
			}));

			this.overviewRows = overview.map(entry => this.toOverviewRow(entry));

			if (this.overviewRows.length === 0) {
				this.clearSelection();
				return;
			}

			const preferredSelectionKey = preferredSelection == null
				? undefined
				: this.getSelectionKey(preferredSelection.releaseFamilyKey, preferredSelection.version, preferredSelection.isPreRelease);
			const nextSelection = preferredSelectionKey != null
				? this.overviewRows.find(entry => entry.selectionKey === preferredSelectionKey)
				: this.selectedOverview != null
					? this.overviewRows.find(entry => entry.selectionKey === this.selectedOverview?.selectionKey)
					: this.overviewRows[0];

			if (nextSelection == null) {
				this.clearSelection();
				return;
			}

			await this.selectRelease(nextSelection, false);
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.loadingOverview = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public async onOverviewSelectionChanged(event: SelectionChangedEvent): Promise<void> {
		const row = event.selectedRowsData?.[0] as ChangelogReleaseOverviewRow | undefined;
		if (row == null) {
			if (this.selectedOverview != null) {
				this.selectedRowKeys = [this.selectedOverview.selectionKey];
				this.changeDetectorRef.markForCheck();
			}
			return;
		}

		if (this.selectedOverview?.selectionKey === row.selectionKey && this.release != null) {
			this.selectedRowKeys = [row.selectionKey];
			return;
		}

		await this.selectRelease(row, true);
	}

	public async refreshSelectedRelease(): Promise<void> {
		if (this.selectedOverview == null) {
			return;
		}

		await this.selectRelease(this.selectedOverview, false);
	}

	public async saveDraft(): Promise<void> {
		await this.saveRelease('Draft');
	}

	public async markReadyForReview(): Promise<void> {
		await this.saveRelease('ReadyForReview');
	}

	public async publish(): Promise<void> {
		await this.saveRelease('Published');
	}

	public async archive(): Promise<void> {
		await this.saveRelease('Archived');
	}

	public async refreshPreview(): Promise<void> {
		if (this.selectedOverview == null) {
			return;
		}

		this.loadingPreview = true;
		this.changeDetectorRef.markForCheck();

		try {
			this.preview = await firstValueFrom(this.changelogService.getReleasePreview(
				this.selectedOverview.releaseFamilyKey,
				this.selectedOverview.version,
				this.selectedOverview.isPreRelease,
				{
					audience: this.previewAudience,
					stage: this.previewStage,
					applicationKeys: this.previewApplicationKeys,
				},
			));
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.loadingPreview = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public resetDraftToSuggested(): void {
		if (this.release == null) {
			return;
		}

		this.editorPayload = this.clonePayload(this.release.suggestedPayload);
		this.isDirty = true;
		this.selectedBulkItemIds = [];
		this.changeDetectorRef.markForCheck();
	}

	public restorePublishedToDraft(): void {
		if (this.release?.publishedPayload == null) {
			return;
		}

		this.editorPayload = this.clonePayload(this.release.publishedPayload);
		this.isDirty = true;
		this.selectedBulkItemIds = [];
		this.changeDetectorRef.markForCheck();
	}

	public addSection(): void {
		if (this.editorPayload == null) {
			return;
		}

		this.editorPayload.sections.push({
			sectionName: 'Notes',
			items: [this.createEmptyItem()],
		});
		this.isDirty = true;
	}

	public removeSection(sectionIndex: number): void {
		if (this.editorPayload == null) {
			return;
		}

		this.editorPayload.sections.splice(sectionIndex, 1);
		this.removeOrphanedBulkSelections();
		this.isDirty = true;
	}

	public addItem(section: GroupedChangelogSectionDto): void {
		section.items.push(this.createEmptyItem());
		this.isDirty = true;
	}

	public removeItem(section: GroupedChangelogSectionDto, itemIndex: number): void {
		const [removedItem] = section.items.splice(itemIndex, 1);
		if (removedItem != null) {
			this.selectedBulkItemIds = this.selectedBulkItemIds.filter(itemId => itemId !== removedItem.id);
		}
		this.isDirty = true;
	}

	public toggleBulkSelection(itemId: string, selected: boolean): void {
		const currentIds = new Set(this.selectedBulkItemIds);
		if (selected) {
			currentIds.add(itemId);
		} else {
			currentIds.delete(itemId);
		}

		this.selectedBulkItemIds = Array.from(currentIds);
	}

	public async applyBulkUpdate(): Promise<void> {
		if (!this.canApplyBulkUpdate || this.selectedOverview == null) {
			return;
		}

		this.applyingBulkUpdate = true;
		this.changeDetectorRef.markForCheck();

		try {
			const request: BulkUpdateChangelogReleaseDto = {
				itemIds: [...this.selectedBulkItemIds],
				sectionName: this.bulkSectionName || undefined,
				visibility: this.bulkVisibility || undefined,
				scope: this.bulkScope || undefined,
				targetApplicationKeys: this.bulkTargetApplicationKeys.length > 0 ? [...this.bulkTargetApplicationKeys] : undefined,
			};

			const release = await firstValueFrom(this.changelogService.bulkUpdateRelease(
				this.selectedOverview.releaseFamilyKey,
				this.selectedOverview.version,
				this.selectedOverview.isPreRelease,
				request,
			));
			this.applyReleaseResponse(release);
			this.selectedBulkItemIds = [];
			this.bulkSectionName = '';
			this.bulkVisibility = '';
			this.bulkScope = '';
			this.bulkTargetApplicationKeys = [];
			await this.refreshPreview();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.applyingBulkUpdate = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public markDirty(): void {
		this.isDirty = true;
	}

	public onItemScopeChanged(item: GroupedChangelogItemDto): void {
		if (item.scope !== 'Application') {
			item.targetApplicationKeys = [];
		}

		this.markDirty();
	}

	public onBulkScopeChanged(): void {
		if (this.bulkScope !== 'Application') {
			this.bulkTargetApplicationKeys = [];
		}
	}

	public setPreviewApplicationKeys(event: Event): void {
		this.previewApplicationKeys = this.getSelectedValues(event);
	}

	public setBulkTargetApplicationKeys(event: Event): void {
		this.bulkTargetApplicationKeys = this.getSelectedValues(event);
	}

	public setItemTargetApplicationKeys(item: GroupedChangelogItemDto, event: Event): void {
		item.targetApplicationKeys = this.getSelectedValues(event);
		if (item.targetApplicationKeys.length > 0) {
			item.scope = 'Application';
		}
		this.markDirty();
	}

	public toggleSourceSelection(sourceId: number, selected: boolean): void {
		const nextSelectedIds = new Set(this.selectedSourceIds);
		if (selected) {
			nextSelectedIds.add(sourceId);
		} else {
			nextSelectedIds.delete(sourceId);
		}

		this.selectedSourceIds = Array.from(nextSelectedIds).sort((left, right) => left - right);
		this.markDirty();
	}

	public selectAllAvailableSources(): void {
		this.selectedSourceIds = (this.release?.availableSources ?? []).map(source => source.id);
		this.markDirty();
	}

	public resetSourceSelectionToSaved(): void {
		this.selectedSourceIds = (this.release?.sources ?? []).map(source => source.id);
		this.markDirty();
	}

	public selectSourcesMatchingVersion(version: string): void {
		this.selectedSourceIds = (this.release?.availableSources ?? [])
			.filter(source => source.version === version)
			.map(source => source.id);
		this.markDirty();
	}

	public isSelectedSource(sourceId: number): boolean {
		return this.selectedSourceIds.includes(sourceId);
	}

	public isBulkSelected(itemId: string): boolean {
		return this.selectedBulkItemIds.includes(itemId);
	}

	public isPreviewApplicationSelected(applicationKey: string): boolean {
		return this.previewApplicationKeys.includes(applicationKey);
	}

	public isBulkTargetApplicationSelected(applicationKey: string): boolean {
		return this.bulkTargetApplicationKeys.includes(applicationKey);
	}

	public isItemTargetApplicationSelected(item: GroupedChangelogItemDto, applicationKey: string): boolean {
		return item.targetApplicationKeys.includes(applicationKey);
	}

	public async inspectSource(sourceId: number): Promise<void> {
		await this.loadSourceDetail(sourceId, true);
	}

	public trackBySectionIndex(index: number): number {
		return index;
	}

	public trackByItemId(_: number, item: GroupedChangelogItemDto): string {
		return item.id;
	}

	public trackBySourceId(_: number, source: ChangelogReleaseSourceDto): number {
		return source.id;
	}

	
	public formatSectionOverview(sections: { sectionName: string; itemCount: number }[]): string {
		return sections.map(section => section.sectionName + ' (' + section.itemCount + ')').join(', ');
	}
	private async selectRelease(row: ChangelogReleaseOverviewRow, promptOnDirty: boolean): Promise<void> {
		if (promptOnDirty && this.isDirty && !this.confirmDiscardChanges()) {
			this.selectedRowKeys = this.selectedOverview != null ? [this.selectedOverview.selectionKey] : [];
			this.changeDetectorRef.markForCheck();
			return;
		}

		this.loadingEntry = true;
		this.selectedRowKeys = [row.selectionKey];
		this.changeDetectorRef.markForCheck();

		try {
			const release = await firstValueFrom(this.changelogService.getRelease(
				row.releaseFamilyKey,
				row.version,
				row.isPreRelease,
			));

			this.selectedOverview = row;
			this.applyReleaseResponse(release);
			this.previewAudience = this.allowedPreviewAudiences[0] ?? 'Users';
			this.previewStage = release.publishedPayload != null ? 'Published' : 'Draft';
			this.previewApplicationKeys = [];

			if (release.sources.length > 0) {
				await this.loadSourceDetail(release.sources[0].id, false);
			} else if (release.availableSources.length > 0) {
				await this.loadSourceDetail(release.availableSources[0].id, false);
			} else {
				this.selectedSourceId = undefined;
				this.selectedSourceDetail = undefined;
			}

			await this.refreshPreview();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.loadingEntry = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private async saveRelease(status: string): Promise<void> {
		if (this.selectedOverview == null || this.editorPayload == null) {
			return;
		}

		this.saving = true;
		this.changeDetectorRef.markForCheck();

		try {
			const release = await firstValueFrom(this.changelogService.saveRelease(
				this.selectedOverview.releaseFamilyKey,
				this.selectedOverview.version,
				this.selectedOverview.isPreRelease,
				{
					status,
					draftPayload: this.clonePayload(this.editorPayload),
					publishedPayload: status === 'Published' ? this.clonePayload(this.editorPayload) : undefined,
					sourceChangelogIds: [...this.selectedSourceIds],
				},
			));
			await this.loadOverview({
				releaseFamilyKey: release.releaseFamilyKey,
				version: release.version,
				isPreRelease: release.isPreRelease,
			});
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.saving = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private async loadSourceDetail(sourceId: number, showErrors: boolean): Promise<void> {
		this.loadingSourceDetail = true;
		this.selectedSourceId = sourceId;
		this.changeDetectorRef.markForCheck();

		try {
			this.selectedSourceDetail = await firstValueFrom(this.changelogService.getSourceById(sourceId));
		} catch (error) {
			this.selectedSourceDetail = undefined;
			if (showErrors) {
				showErrorSwal(error);
			}
		} finally {
			this.loadingSourceDetail = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private applyReleaseResponse(release: ChangelogReleaseEditorDto): void {
		this.release = release;
		this.editorPayload = this.clonePayload(release.draftPayload);
		this.isDirty = false;
		this.selectedBulkItemIds = [];
		this.selectedSourceIds = release.sources.map(source => source.id);
		this.bulkSectionName = '';
		this.bulkVisibility = '';
		this.bulkScope = '';
		this.bulkTargetApplicationKeys = [];
		this.removeOrphanedBulkSelections();
	}

	private clearSelection(): void {
		this.selectedOverview = undefined;
		this.release = undefined;
		this.editorPayload = undefined;
		this.preview = undefined;
		this.selectedSourceId = undefined;
		this.selectedSourceDetail = undefined;
		this.selectedRowKeys = [];
		this.selectedBulkItemIds = [];
		this.selectedSourceIds = [];
		this.bulkSectionName = '';
		this.bulkVisibility = '';
		this.bulkScope = '';
		this.bulkTargetApplicationKeys = [];
		this.previewApplicationKeys = [];
		this.isDirty = false;
	}

	private toOverviewRow(entry: ChangelogReleaseOverviewDto): ChangelogReleaseOverviewRow {
		return {
			...entry,
			selectionKey: this.getSelectionKey(entry.releaseFamilyKey, entry.version, entry.isPreRelease),
			sourceApplicationsText: entry.sourceApplicationKeys.join(', '),
			highlightsText: entry.highlights.join(' | '),
		};
	}

	private clonePayload(payload: GroupedChangelogPayloadDto): GroupedChangelogPayloadDto {
		return JSON.parse(JSON.stringify(payload)) as GroupedChangelogPayloadDto;
	}

	private createEmptyItem(): GroupedChangelogItemDto {
		return {
			id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
			text: '',
			visibility: 'Users',
			scope: 'Global',
			targetApplicationKeys: [],
			isManuallyEdited: true,
			sourceReferences: [],
		};
	}

	private removeOrphanedBulkSelections(): void {
		if (this.editorPayload == null) {
			this.selectedBulkItemIds = [];
			return;
		}

		const validIds = new Set(
			this.editorPayload.sections.reduce((itemIds, section) => itemIds.concat(section.items.map(item => item.id)), [] as string[]),
		);

		this.selectedBulkItemIds = this.selectedBulkItemIds.filter(itemId => validIds.has(itemId));
	}

	private getSelectionKey(releaseFamilyKey: string, version: string, isPreRelease: boolean): string {
		return `${releaseFamilyKey}::${version}::${isPreRelease}`;
	}

	private getSelectedValues(event: Event): string[] {
		const target = event.target as HTMLSelectElement | null;
		if (target == null) {
			return [];
		}

		return Array.from(target.selectedOptions).map(option => option.value);
	}

	private confirmDiscardChanges(): boolean {
		return globalThis.confirm == null
			? true
			: globalThis.confirm('Discard unsaved grouped changelog draft changes?');
	}
}








